import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'open', label: 'Aperto', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Lavorazione', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'closed', label: 'Chiuso', color: 'bg-green-100 text-green-700' },
];

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, page]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '10' });
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await axios.get(`${API}/admin/tickets?${params}`);
      setTickets(res.data.tickets);
      setTotalPages(res.data.pages);
    } catch (error) {
      toast.error('Errore nel caricamento ticket');
    } finally {
      setLoading(false);
    }
  };

  const openTicketDialog = (ticket) => {
    setSelectedTicket(ticket);
    setResponse(ticket.admin_response || '');
    setDialogOpen(true);
  };

  const handleUpdateTicket = async (newStatus = null) => {
    if (!selectedTicket) return;
    
    setSending(true);
    try {
      const update = {};
      if (newStatus) update.status = newStatus;
      if (response !== selectedTicket.admin_response) update.admin_response = response;
      
      await axios.put(`${API}/admin/tickets/${selectedTicket.id}`, update);
      toast.success('Ticket aggiornato');
      setDialogOpen(false);
      fetchTickets();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return <Badge className={opt?.color || 'bg-gray-100 text-gray-700'}>{opt?.label || status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif">Ticket Supporto</h1>
          <p className="text-muted-foreground mt-1">Gestisci le richieste dei clienti</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
          ))
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-border">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun ticket trovato</p>
          </div>
        ) : (
          tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="bg-white rounded-xl border border-border p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openTicketDialog(ticket)}
              data-testid={`ticket-${ticket.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold truncate">{ticket.subject}</h3>
                    {getStatusBadge(ticket.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Da: {ticket.customer_name} ({ticket.customer_email})
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {ticket.message}
                  </p>
                  {ticket.admin_response && (
                    <div className="mt-3 p-3 bg-primary/5 rounded-lg">
                      <p className="text-xs font-medium text-primary mb-1">Risposta inviata:</p>
                      <p className="text-sm line-clamp-1">{ticket.admin_response}</p>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-muted-foreground">
                    {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                  </p>
                  {ticket.order_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Ordine: #{ticket.order_id.slice(0, 8)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Precedente
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Successivo
          </Button>
        </div>
      )}

      {/* Ticket Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ticket #{selectedTicket?.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Stato:</span>
                <Select
                  value={selectedTicket.status}
                  onValueChange={(val) => setSelectedTicket(prev => ({ ...prev, status: val }))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Da:</p>
                <p className="font-medium">{selectedTicket.customer_name}</p>
                <p className="text-sm">{selectedTicket.customer_email}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Oggetto:</p>
                <p className="font-medium">{selectedTicket.subject}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Messaggio:</p>
                <p className="bg-muted/30 rounded-lg p-4">{selectedTicket.message}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">La tua risposta:</p>
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  rows={5}
                  placeholder="Scrivi una risposta al cliente..."
                  data-testid="ticket-response"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Chiudi
            </Button>
            <Button
              onClick={() => handleUpdateTicket(selectedTicket?.status)}
              disabled={sending}
              data-testid="save-ticket-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Salva e Invia
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
